import { useCallback, useRef, useState } from 'react'
import { IconButton } from '@features/shared/components/IconButton'
import Popover from '@features/shared/components/Popover'
import { COMPACT_POPOVER_CONTENT_CLASS } from '@features/shared/components/compactDropdownStyles'
import { PopoverMenu } from '@features/shared/components/PopoverMenu'
import { UploadIcon } from '@features/shared/icons/icons'
import type { ExportScope } from '../api/contracts'
import {
  useMarkSeenOnOpen,
  useRecentExports,
  useRegisterExportMenu,
} from '../hooks/useRecentExports'
import { exportMenuDot, exportMenuTooltip, runningSummary } from '../logic/exportStatus'
import ExportMenuIcon from './ExportMenuIcon'
import RecentExports from './RecentExports'
import { useExportWatchStore } from '../stores/useExportWatchStore'

const ALL: ExportScope = { all: true }

/**
 * The header's Exports (docs/specs/EXPORTS.md, The Export menu): every
 * export of the person's, across tables and lists, newest first, each named
 * with its table or list, with everything a Recent Exports row has (progress,
 * download, drag, selection, cancel, remove). It doesn't start exports; a
 * table's or list's Export menu does. Its dot is amber while any export runs
 * and blue once one is ready and unseen; opening it marks them seen.
 */
export default function ExportsTray({ className }: { className: (open: boolean) => string }) {
  const buttonRef = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  // Opened on its own (Open When Ready): focus stays where the person is.
  const [quiet, setQuiet] = useState(false)
  const { data: exports = [] } = useRecentExports(ALL, { open })
  useMarkSeenOnOpen(ALL, open, exports)
  useRegisterExportMenu(ALL, buttonRef, () => {
    setQuiet(true)
    setOpen(true)
  })
  const onOpenChange = useCallback((next: boolean) => {
    setQuiet(false)
    setOpen(next)
  }, [])

  // Amber at once for an export still being queued, before the list has it.
  const starting = useExportWatchStore((state) => Object.keys(state.starting).length > 0)
  const dot = starting ? 'running' : exportMenuDot(exports)

  return (
    <span ref={buttonRef} className="inline-flex shrink-0">
      <Popover
        open={open}
        onOpenChange={onOpenChange}
        align="right"
        showCaret={false}
        contentRole="menu"
        focusOnOpen={!quiet}
        wrapperClassName="inline-flex shrink-0"
        contentClassName={COMPACT_POPOVER_CONTENT_CLASS}
        trigger={
          <IconButton
            label="Exports"
            tooltip={exportMenuTooltip('Exports', dot, runningSummary(exports))}
            aria-haspopup="menu"
            aria-expanded={open}
            wrapperClassName="shrink-0"
            className={className(open)}
          >
            <ExportMenuIcon icon={<UploadIcon size={17} aria-hidden="true" />} dot={dot} />
          </IconButton>
        }
      >
        <PopoverMenu className="w-80">
          {exports.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted">
              No recent exports. Export a table or list from its Export menu.
            </p>
          ) : (
            <RecentExports
              exports={exports}
              collapsible={false}
              divider={false}
              onDialog={() => onOpenChange(false)}
            />
          )}
        </PopoverMenu>
      </Popover>
    </span>
  )
}
